import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Shield,
  Award,
  CheckCircle2,
  XCircle,
  Calendar,
  Trophy,
  Star,
  Loader2,
  ArrowLeft,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CertificateVerify = () => {
  const { certificateNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyCertificate = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/rpat/certification/verify/${certificateNumber}`);
        setCertificate(res.data);
      } catch (err) {
        console.error('Verification failed:', err);
        setError(err.response?.data?.detail || 'Certificate not found or invalid');
      } finally {
        setLoading(false);
      }
    };

    if (certificateNumber) {
      verifyCertificate();
    }
  }, [certificateNumber]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-cyan-500 mx-auto mb-4" />
          <p className="text-white text-lg">Verifying certificate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/95 backdrop-blur">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="inline-flex p-4 rounded-full bg-red-100 mb-4">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Invalid Certificate</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <p className="text-sm text-gray-500 mb-4">
              Certificate Number: <code className="bg-gray-100 px-2 py-1 rounded">{certificateNumber}</code>
            </p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Verification Badge */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-4 py-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <span className="text-emerald-300 font-medium">Verified Certificate</span>
          </div>
        </div>

        {/* Certificate Card */}
        <Card className="bg-white/95 backdrop-blur border-4 border-double border-cyan-600 shadow-2xl">
          <CardContent className="p-8">
            {/* Header */}
            <div className="text-center border-b-2 border-cyan-200 pb-6 mb-6">
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <Shield className="h-16 w-16 text-cyan-600" />
                  <Award className="h-8 w-8 text-amber-500 absolute -bottom-1 -right-1" />
                </div>
              </div>
              <h1 className="text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-2">
                CERTIFICATE OF ACHIEVEMENT
              </h1>
              <p className="text-cyan-600 font-medium tracking-widest text-sm">
                TEKWISSEN - CERTIFIED RECRUITER
              </p>
            </div>

            {/* Certificate Details */}
            <div className="text-center mb-8">
              <p className="text-gray-600 mb-3">This certificate verifies that</p>
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-4 border-b-2 border-amber-400 inline-block pb-2 px-6">
                {certificate.recipient_name}
              </h2>
              <p className="text-gray-600 mb-3">
                has successfully completed the
              </p>
              <h3 className="text-xl font-semibold text-cyan-700 mb-3">
                R-PAT: Recruitment Process Autonomous Training
              </h3>
              <p className="text-gray-600 mb-2">
                with specialization in
              </p>
              <Badge className="text-base px-4 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-600">
                {certificate.specialization}
              </Badge>
            </div>

            {/* Achievement Details */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-3 bg-cyan-50 rounded-lg text-center">
                <Trophy className="h-5 w-5 mx-auto text-amber-500 mb-1" />
                <p className="text-xs text-gray-600">Final Score</p>
                <p className="font-bold text-cyan-700">{certificate.score}%</p>
              </div>
              <div className="p-3 bg-cyan-50 rounded-lg text-center">
                <Calendar className="h-5 w-5 mx-auto text-cyan-600 mb-1" />
                <p className="text-xs text-gray-600">Issue Date</p>
                <p className="font-bold text-cyan-700">
                  {new Date(certificate.issued_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <Star className="h-5 w-5 mx-auto text-emerald-500 mb-1" />
                <p className="text-xs text-gray-600">Status</p>
                <p className="font-bold text-emerald-600">Valid</p>
              </div>
            </div>

            {/* Certificate Number */}
            <div className="border-t-2 border-cyan-200 pt-4 text-center">
              <p className="text-xs text-gray-500 mb-1">Certificate Number</p>
              <p className="font-mono font-bold text-cyan-700 text-lg">{certificate.certificate_number}</p>
            </div>
          </CardContent>
        </Card>

        {/* Footer Info */}
        <div className="text-center mt-6">
          <p className="text-gray-400 text-sm mb-4">
            This certificate is issued by TekWissen Group and can be independently verified.
          </p>
          <a 
            href="https://tekwissen.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm"
          >
            Learn more about TekWissen
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default CertificateVerify;
